import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Breadcrumb,
  Card,
  Descriptions,
  Tag,
  Typography,
  Collapse,
  Row,
  Col,
} from "antd";
import { useBucketDataDetail } from "../../../../src/hooks/api/filling-management/hooks";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const BucketDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: detailResponse, isLoading } = useBucketDataDetail(id ?? "");
  const detail = detailResponse?.data;

  if (isLoading) {
    return <div className="p-4">Loading details...</div>;
  }

  if (!detail) {
    return <div className="p-4">No details found.</div>;
  }

  const {
    formDetails,
    complexity,
    status,
    filling_year,
    filling_type,
    receipt_no,
    progress,
    current_step,
    approval,
    quarter,
  } = detail;

  const crystallised = (formDetails as any)?.crystallised || false;
  const crystallised_at = (formDetails as any)?.crystallised_at || null;
  const calculation_id = (formDetails as any)?.calculation_id || null;
  const quarterlyData = (formDetails as any)?.quarterlyData || null;

  const formatDate = (date: string) =>
    date ? dayjs(date).format("DD MMM YYYY, HH:mm") : "-";

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-lg sm:text-3xl font-semibold m-0">
            Bucket Details
          </h1>
          <Breadcrumb
            separator=">"
            items={[
              { title: "Dashboard" },
              {
                title: "Filling Management",
                onClick: () => navigate(-1),
                className: "cursor-pointer",
              },
              { title: "Details" },
            ]}
            className="text-xs sm:text-base mt-2"
          />
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap gap-2">
          <Tag
            color={status === "PAID" ? "purple" : status === "SUBMITTED" ? "green" : "blue"}
            className="text-sm px-3 py-1 mt-1 font-semibold"
          >
            {status === "PAID" ? "💳 Paid" : status === "SUBMITTED" ? "Submitted" : "In Progress"}
          </Tag>
          <Tag
            color={complexity?.level === "HIGH" ? "red" : complexity?.level === "MEDIUM" ? "orange" : "cyan"}
            className="text-sm px-3 py-1 mt-1 font-semibold"
          >
            {complexity?.level || "LOW"} Complexity
          </Tag>
          {crystallised && (
            <Tag color="green" className="text-sm px-3 py-1 mt-1 font-semibold">
              ✓ Crystallised
            </Tag>
          )}
          {filling_type && (
            <Tag color="geekblue" className="text-sm px-3 py-1 mt-1 font-semibold">
              {filling_type}
            </Tag>
          )}
        </div>
      </div>

      <Card className="mb-6 shadow-sm custom_shadow border-0">
        <Descriptions
          title="General Information"
          layout="vertical"
          column={{ xs: 1, sm: 2, md: 4 }}
        >
          <Descriptions.Item label="Receipt No">
            {receipt_no || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Filling Year">
            {filling_year}
          </Descriptions.Item>
          <Descriptions.Item label="Filing Type">
            {filling_type || "-"}
          </Descriptions.Item>
          {quarter && (
            <Descriptions.Item label="Quarter">{quarter}</Descriptions.Item>
          )}
          <Descriptions.Item label="Progress">{progress}%</Descriptions.Item>
          <Descriptions.Item label="Current Step">
            {current_step}
          </Descriptions.Item>
          <Descriptions.Item label="Payment">
            <Tag color={status === "PAID" ? "purple" : "default"}>
              {status === "PAID" ? "Paid" : status === "SUBMITTED" ? "Awaiting Payment" : "Not Paid"}
            </Tag>
          </Descriptions.Item>
          {approval && (
            <Descriptions.Item label="Admin Approval">
              <Tag color={approval.status === "APPROVED" ? "green" : approval.status === "PENDING" ? "orange" : "default"}>
                {approval.required ? approval.status : "Not Required"}
              </Tag>
            </Descriptions.Item>
          )}
        </Descriptions>

        {crystallised && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm font-semibold text-green-800 mb-1">✓ Return Crystallised</p>
            {crystallised_at && (
              <p className="text-xs text-green-700">Crystallised at: {formatDate(crystallised_at)}</p>
            )}
            {calculation_id && (
              <p className="text-xs text-green-600 font-mono mt-1">Calculation ID: {calculation_id}</p>
            )}
          </div>
        )}
      </Card>

      <Collapse
        defaultActiveKey={[
          "step1",
          "step2",
          "step3",
          "step4",
          "step5",
          "step6",
          "quarterly",
        ]}
        className="bg-white shadow-sm custom_shadow border-0"
        size="large"
      >
        {/* Step 1 */}
        {formDetails?.stepOne && (
          <Collapse.Panel
            header={
              <Title level={5} className="m-0 text-gray-800">
                Step 1: Personal Details
              </Title>
            }
            key="step1"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Text type="secondary">Name</Text>
                <div>
                  <Text strong>{formDetails.stepOne.name}</Text>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary">UTR</Text>
                <div>
                  <Text strong>{formDetails.stepOne.UTR}</Text>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary">NIN</Text>
                <div>
                  <Text strong>{formDetails.stepOne.nin}</Text>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary">Date of Birth</Text>
                <div>
                  <Text strong>
                    {formatDate(formDetails.stepOne.date_of_birth)}
                  </Text>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary">Country of Birth</Text>
                <div>
                  <Text strong>{formDetails.stepOne.country_of_birth}</Text>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary">Residence Status</Text>
                <div>
                  <Text strong>{formDetails.stepOne.residence_status}</Text>
                </div>
              </Col>
              <Col xs={24}>
                <Text type="secondary">Address</Text>
                <div>
                  <Text strong>{formDetails.stepOne.address}</Text>
                </div>
              </Col>
            </Row>
          </Collapse.Panel>
        )}

        {/* Step 2 */}
        {formDetails?.stepTwo && (
          <Collapse.Panel
            header={
              <Title level={5} className="m-0 text-gray-800">
                Step 2: Employment & Benefits
              </Title>
            }
            key="step2"
          >
            {formDetails.stepTwo.employers &&
              formDetails.stepTwo.employers.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Employers
                    </Text>
                  </div>
                  {formDetails.stepTwo.employers.map(
                    (emp: any, index: number) => (
                      <Card
                        size="small"
                        type="inner"
                        title={
                          <span className="text-primary">
                            {emp.employer_name}
                          </span>
                        }
                        key={index}
                        className="mb-4 bg-gray-50/50"
                      >
                        <Row gutter={[16, 16]}>
                          <Col xs={24} md={8}>
                            <Text type="secondary">Tax Reference</Text>
                            <div>
                              <Text strong>{emp.employer_tax_reference}</Text>
                            </div>
                          </Col>
                          <Col xs={24} md={8}>
                            <Text type="secondary">Duration</Text>
                            <div>
                              <Text strong>
                                {formatDate(emp.employer_start)} -{" "}
                                {formatDate(emp.employer_end)}
                              </Text>
                            </div>
                          </Col>
                          <Col xs={24} md={8}>
                            <Text type="secondary">Earnings</Text>
                            <div>
                              <Text strong>£{emp.earnings}</Text>
                            </div>
                          </Col>
                          <Col xs={24} md={8}>
                            <Text type="secondary">Tax Paid</Text>
                            <div>
                              <Text strong>£{emp.tax_paid}</Text>
                            </div>
                          </Col>
                          <Col xs={24} md={16}>
                            <Text type="secondary">Address</Text>
                            <div>
                              <Text strong>{emp.employer_address}</Text>
                            </div>
                          </Col>
                        </Row>
                      </Card>
                    ),
                  )}
                </div>
              )}

            {formDetails.stepTwo.have_benefits_expanses &&
              formDetails.stepTwo.benefits_expenses &&
              formDetails.stepTwo.benefits_expenses.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Benefits & Expenses
                    </Text>
                  </div>
                  {formDetails.stepTwo.benefits_expenses.map(
                    (be: any, index: number) => (
                      <Row
                        gutter={[16, 16]}
                        key={index}
                        className="bg-gray-50/50 p-4 border rounded-lg mb-4"
                      >
                        <Col xs={24} md={8}>
                          <Text type="secondary">Description</Text>
                          <div>
                            <Text strong>{be.description}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Value of Benefit</Text>
                          <div>
                            <Text strong>£{be.value_of_benefit}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Expense Amount</Text>
                          <div>
                            <Text strong>£{be.expense_amount}</Text>
                          </div>
                        </Col>
                      </Row>
                    ),
                  )}
                </div>
              )}

            {formDetails.stepTwo.have_car_Benefits &&
              formDetails.stepTwo.car_benefits &&
              formDetails.stepTwo.car_benefits.length > 0 && (
                <div className="mb-2">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Car Benefits
                    </Text>
                  </div>
                  {formDetails.stepTwo.car_benefits.map(
                    (cb: any, index: number) => (
                      <Row
                        gutter={[16, 16]}
                        key={index}
                        className="bg-gray-50/50 p-4 border rounded-lg mb-4"
                      >
                        <Col xs={24} md={8}>
                          <Text type="secondary">Model</Text>
                          <div>
                            <Text strong>{cb.model}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Registration</Text>
                          <div>
                            <Text strong>{cb.registration}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Registered Date</Text>
                          <div>
                            <Text strong>{formatDate(cb.registered_date)}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Engine Type</Text>
                          <div>
                            <Text strong>{cb.engine_type}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">List Price</Text>
                          <div>
                            <Text strong>£{cb.list_price}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Business Mileage</Text>
                          <div>
                            <Text strong>{cb.business_mileage}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Contribution</Text>
                          <div>
                            <Text strong>£{cb.contribution}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Value of Accessories</Text>
                          <div>
                            <Text strong>£{cb.Value_of_accessories}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Duration</Text>
                          <div>
                            <Text strong>
                              {formatDate(cb.date_from)} -{" "}
                              {formatDate(cb.date_to)}
                            </Text>
                          </div>
                        </Col>
                      </Row>
                    ),
                  )}
                </div>
              )}
          </Collapse.Panel>
        )}

        {/* Step 3 */}
        {formDetails?.stepThree && (
          <Collapse.Panel
            header={
              <Title level={5} className="m-0 text-gray-800">
                Step 3: Income
              </Title>
            }
            key="step3"
          >
            {formDetails.stepThree.have_self_employment &&
              formDetails.stepThree.self_employment && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Self Employment
                    </Text>
                  </div>
                  <div className="bg-gray-50/50 p-4 border rounded-lg mb-4">
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <Text type="secondary">Description</Text>
                        <div>
                          <Text strong>
                            {formDetails.stepThree.self_employment.description}
                          </Text>
                        </div>
                      </Col>

                      <Col xs={24} md={6}>
                        <Text type="secondary">Gross</Text>
                        <div>
                          <Text strong>
                            £{formDetails.stepThree.self_employment.gross}
                          </Text>
                        </div>
                      </Col>

                      <Col xs={24} md={6}>
                        <Text type="secondary">Capital Allowance</Text>

                        <div className="space-y-2">
                          {formDetails?.stepThree?.self_employment
                            ?.capital_allowance?.length > 0 ? (
                            formDetails.stepThree.self_employment.capital_allowance.map(
                              (item: any, index: any) => (
                                <div
                                  key={index}
                                  className="border p-2 rounded bg-white"
                                >
                                  <Text strong>{item.description}</Text>
                                  <div>
                                    <Text>£{item.amount}</Text>
                                  </div>
                                </div>
                              ),
                            )
                          ) : (
                            <Text type="secondary">No capital allowance</Text>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {formDetails.stepThree.self_employment
                    .deductible_expenses && (
                    <Card size="small" type="inner" title="Deductible Expenses">
                      <Row gutter={[16, 16]}>
                        <Col span={8}>
                          <Text type="secondary">Travel</Text>
                          <div>
                            <Text strong>
                              £
                              {
                                formDetails.stepThree.self_employment
                                  .deductible_expenses.travel
                              }
                            </Text>
                          </div>
                        </Col>
                        <Col span={8}>
                          <Text type="secondary">User Phone</Text>
                          <div>
                            <Text strong>
                              £
                              {
                                formDetails.stepThree.self_employment
                                  .deductible_expenses.user_phone
                              }
                            </Text>
                          </div>
                        </Col>
                        <Col span={8}>
                          <Text type="secondary">Home as Office</Text>
                          <div>
                            <Text strong>
                              £
                              {
                                formDetails.stepThree.self_employment
                                  .deductible_expenses.use_home_as_office
                              }
                            </Text>
                          </div>
                        </Col>
                      </Row>
                      {formDetails.stepThree.self_employment.deductible_expenses
                        .other &&
                        formDetails.stepThree.self_employment
                          .deductible_expenses.other.length > 0 && (
                          <div className="mt-4">
                            <Text strong>Other Expenses</Text>
                            {formDetails.stepThree.self_employment.deductible_expenses.other.map(
                              (o: any, i: number) => (
                                <Row key={i} className="mt-2 text-sm">
                                  <Col span={12}>
                                    <Text type="secondary">
                                      {o.description}
                                    </Text>
                                  </Col>
                                  <Col span={12}>
                                    <Text strong>£{o.amount}</Text>
                                  </Col>
                                </Row>
                              ),
                            )}
                          </div>
                        )}
                    </Card>
                  )}
                </div>
              )}

            {formDetails.stepThree.saving_income &&
              formDetails.stepThree.saving_income.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Savings Income
                    </Text>
                  </div>
                  {formDetails.stepThree.saving_income.map(
                    (si: any, index: number) => (
                      <Row
                        gutter={[16, 16]}
                        key={index}
                        className="bg-gray-50/50 p-4 border rounded-lg mb-4"
                      >
                        <Col xs={24} md={12}>
                          <Text type="secondary">Description</Text>
                          <div>
                            <Text strong>{si.description}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          <Text type="secondary">Gross</Text>
                          <div>
                            <Text strong>£{si.gross}</Text>
                          </div>
                        </Col>
                      </Row>
                    ),
                  )}
                </div>
              )}

            {formDetails.stepThree.have_dividends_income &&
              formDetails.stepThree.dividends_income && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Dividends Income
                    </Text>
                    <Tag color="cyan">
                      Type: {formDetails.stepThree.dividends_income.type}
                    </Tag>
                  </div>
                  <Row gutter={16}>
                    {formDetails.stepThree.dividends_income.local &&
                      formDetails.stepThree.dividends_income.local.length >
                        0 && (
                        <Col xs={24} md={12}>
                          <Card size="small" type="inner" title="Local">
                            {formDetails.stepThree.dividends_income.local.map(
                              (l: any, i: number) => (
                                <Row gutter={[16, 16]} key={i} className="mb-4">
                                  <Col span={24}>
                                    <Text type="secondary">Description</Text>
                                    <div>
                                      <Text strong>{l.description}</Text>
                                    </div>
                                  </Col>
                                  <Col span={12}>
                                    <Text type="secondary">Shares</Text>
                                    <div>
                                      <Text strong>{l.shares}</Text>
                                    </div>
                                  </Col>
                                  <Col span={12}>
                                    <Text type="secondary">Net</Text>
                                    <div>
                                      <Text strong>£{l.net}</Text>
                                    </div>
                                  </Col>
                                </Row>
                              ),
                            )}
                          </Card>
                        </Col>
                      )}
                    {formDetails.stepThree.dividends_income.foreign &&
                      formDetails.stepThree.dividends_income.foreign.length >
                        0 && (
                        <Col xs={24} md={12}>
                          <Card size="small" type="inner" title="Foreign">
                            {formDetails.stepThree.dividends_income.foreign.map(
                              (f: any, i: number) => (
                                <Row gutter={[16, 16]} key={i} className="mb-4">
                                  <Col span={24}>
                                    <Text type="secondary">Description</Text>
                                    <div>
                                      <Text strong>{f.description}</Text>
                                    </div>
                                  </Col>
                                  <Col span={12}>
                                    <Text type="secondary">Shares</Text>
                                    <div>
                                      <Text strong>{f.shares}</Text>
                                    </div>
                                  </Col>
                                  <Col span={12}>
                                    <Text type="secondary">Net</Text>
                                    <div>
                                      <Text strong>£{f.net}</Text>
                                    </div>
                                  </Col>
                                </Row>
                              ),
                            )}
                          </Card>
                        </Col>
                      )}
                  </Row>
                </div>
              )}

            {formDetails.stepThree.have_trust_and_settlement &&
              formDetails.stepThree.trust_and_settlement &&
              formDetails.stepThree.trust_and_settlement.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Trust & Settlement
                    </Text>
                  </div>
                  {formDetails.stepThree.trust_and_settlement.map(
                    (ts: any, index: number) => (
                      <Row
                        gutter={[16, 16]}
                        key={index}
                        className="bg-gray-50/50 p-4 border rounded-lg mb-4"
                      >
                        <Col xs={24} md={8}>
                          <Text type="secondary">Description</Text>
                          <div>
                            <Text strong>{ts.description}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Gross</Text>
                          <div>
                            <Text strong>£{ts.gross}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Tax Paid</Text>
                          <div>
                            <Text strong>£{ts.tax_paid}</Text>
                          </div>
                        </Col>
                      </Row>
                    ),
                  )}
                </div>
              )}

            {formDetails.stepThree.have_land_and_property &&
              formDetails.stepThree.land_and_property &&
              formDetails.stepThree.land_and_property.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Land & Property
                    </Text>
                  </div>
                  {formDetails.stepThree.land_and_property.map(
                    (lp: any, index: number) => (
                      <div
                        key={index}
                        className="bg-gray-50/50 p-4 border rounded-lg mb-4"
                      >
                        <Row gutter={[16, 16]}>
                          <Col xs={24} md={12}>
                            <Text type="secondary">Description</Text>
                            <div>
                              <Text strong>{lp.description}</Text>
                            </div>
                          </Col>
                          <Col xs={24} md={12}>
                            <Text type="secondary">Income Received</Text>
                            <div>
                              <Text strong>£{lp.income_received}</Text>
                            </div>
                          </Col>
                        </Row>
                        {lp.deductible_expenses && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <Text type="secondary" className="mb-2 block">
                              Deductible Expenses
                            </Text>
                            <Row gutter={[16, 16]}>
                              <Col xs={24} md={8}>
                                <Text type="secondary">
                                  Maintenance & Repairs
                                </Text>
                                <div>
                                  <Text strong>
                                    £
                                    {lp.deductible_expenses.maintenance_repairs}
                                  </Text>
                                </div>
                              </Col>
                              <Col xs={24} md={8}>
                                <Text type="secondary">
                                  Loan/Interest Finance
                                </Text>
                                <div>
                                  <Text strong>
                                    £
                                    {
                                      lp.deductible_expenses
                                        .loan_interest_finance
                                    }
                                  </Text>
                                </div>
                              </Col>
                              <Col xs={24} md={8}>
                                <Text type="secondary">Other</Text>
                                <div>
                                  <Text strong>
                                    £{lp.deductible_expenses.other}
                                  </Text>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        )}
                      </div>
                    ),
                  )}
                </div>
              )}

            {formDetails.stepThree.have_foreign_income &&
              formDetails.stepThree.foreign_income &&
              formDetails.stepThree.foreign_income.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Foreign Income
                    </Text>
                  </div>
                  {formDetails.stepThree.foreign_income.map(
                    (fi: any, index: number) => (
                      <Row
                        gutter={[16, 16]}
                        key={index}
                        className="bg-gray-50/50 p-4 border rounded-lg mb-4"
                      >
                        <Col xs={24} md={8}>
                          <Text type="secondary">Description</Text>
                          <div>
                            <Text strong>{fi.description}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Income Type</Text>
                          <div>
                            <Text strong>{fi.income_type}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Net</Text>
                          <div>
                            <Text strong>£{fi.net}</Text>
                          </div>
                        </Col>
                      </Row>
                    ),
                  )}
                </div>
              )}

            {formDetails.stepThree.have_other_foreign_income &&
              formDetails.stepThree.other_foreign_income &&
              formDetails.stepThree.other_foreign_income.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Other Foreign Income
                    </Text>
                  </div>
                  {formDetails.stepThree.other_foreign_income.map(
                    (ofi: any, index: number) => (
                      <Row
                        gutter={[16, 16]}
                        key={index}
                        className="bg-gray-50/50 p-4 border rounded-lg mb-4"
                      >
                        <Col xs={24} md={6}>
                          <Text type="secondary">Interest</Text>
                          <div>
                            <Text strong>£{ofi.interest}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={6}>
                          <Text type="secondary">Dividends</Text>
                          <div>
                            <Text strong>£{ofi.dividends}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={6}>
                          <Text type="secondary">Capital Gains</Text>
                          <div>
                            <Text strong>£{ofi.capital_gains}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={6}>
                          <Text type="secondary">Tax Paid</Text>
                          <div>
                            <Text strong>£{ofi.tax_paid}</Text>
                          </div>
                        </Col>
                      </Row>
                    ),
                  )}
                </div>
              )}

            {formDetails.stepThree.have_other_income &&
              formDetails.stepThree.other_income &&
              formDetails.stepThree.other_income.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Other Income
                    </Text>
                  </div>
                  {formDetails.stepThree.other_income.map(
                    (oi: any, index: number) => (
                      <Row
                        gutter={[16, 16]}
                        key={index}
                        className="bg-gray-50/50 p-4 border rounded-lg mb-4"
                      >
                        <Col xs={24} md={8}>
                          <Text type="secondary">Description</Text>
                          <div>
                            <Text strong>{oi.description}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Income Type</Text>
                          <div>
                            <Text strong>{oi.income_type}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Net</Text>
                          <div>
                            <Text strong>£{oi.net}</Text>
                          </div>
                        </Col>
                      </Row>
                    ),
                  )}
                </div>
              )}
          </Collapse.Panel>
        )}

        {/* Step 4 */}
        {formDetails?.stepFour && (
          <Collapse.Panel
            header={
              <Title level={5} className="m-0 text-gray-800">
                Step 4: Reliefs, Pensions & Charity
              </Title>
            }
            key="step4"
          >
            {formDetails.stepFour.have_pension &&
              formDetails.stepFour.pensions &&
              formDetails.stepFour.pensions.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Pensions
                    </Text>
                  </div>
                  {formDetails.stepFour.pensions.map(
                    (p: any, index: number) => (
                      <Row
                        gutter={[16, 16]}
                        key={index}
                        className="bg-gray-50/50 p-4 border rounded-lg mb-4"
                      >
                        <Col xs={24} md={8}>
                          <Text type="secondary">Pension Handler</Text>
                          <div>
                            <Text strong>{p.pension_handler}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Reference No</Text>
                          <div>
                            <Text strong>{p.reference_no}</Text>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <Text type="secondary">Net</Text>
                          <div>
                            <Text strong>£{p.net}</Text>
                          </div>
                        </Col>
                      </Row>
                    ),
                  )}
                </div>
              )}

            {formDetails.stepFour.have_gift_paid &&
              formDetails.stepFour.gift_aid_payment &&
              formDetails.stepFour.gift_aid_payment.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Gift Aid Payments
                    </Text>
                  </div>
                  {formDetails.stepFour.gift_aid_payment.map(
                    (g: any, i: number) => (
                      <Card
                        size="small"
                        type="inner"
                        key={i}
                        className="mb-3 bg-gray-50/50 border"
                      >
                        <Row>
                          <Col span={12}>
                            <Text type="secondary">Description</Text>
                            <div>
                              <Text strong>{g.description}</Text>
                            </div>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary">Amount Paid</Text>
                            <div>
                              <Text strong>£{g.amount_paid}</Text>
                            </div>
                          </Col>
                        </Row>
                      </Card>
                    ),
                  )}
                </div>
              )}

            {formDetails.stepFour.charity &&
              formDetails.stepFour.charity.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Charity
                    </Text>
                  </div>
                  {formDetails.stepFour.charity.map((c: any, i: number) => (
                    <Card
                      size="small"
                      type="inner"
                      key={i}
                      className="mb-3 bg-gray-50/50 border"
                    >
                      <Row gutter={[16, 16]}>
                        <Col span={8}>
                          <Text type="secondary">Description</Text>
                          <div>
                            <Text strong>{c.description}</Text>
                          </div>
                        </Col>
                        <Col span={8}>
                          <Text type="secondary">Date Made</Text>
                          <div>
                            <Text strong>{formatDate(c.date_made)}</Text>
                          </div>
                        </Col>
                        <Col span={8}>
                          <Text type="secondary">Net Amount</Text>
                          <div>
                            <Text strong>£{c.net_amount_paid}</Text>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>
              )}

            {formDetails.stepFour.have_other_reliefs &&
              formDetails.stepFour.other_reliefs &&
              formDetails.stepFour.other_reliefs.length > 0 && (
                <div className="mb-6">
                  <div className="flex gap-2 mb-3 items-center">
                    <div className="w-1 h-4 bg-primary rounded-full"></div>
                    <Text strong className="text-base text-gray-700">
                      Other Reliefs
                    </Text>
                  </div>
                  {formDetails.stepFour.other_reliefs.map(
                    (o: any, i: number) => (
                      <Card
                        size="small"
                        type="inner"
                        key={i}
                        className="mb-3 bg-gray-50/50 border"
                      >
                        <Row gutter={[16, 16]}>
                          <Col span={8}>
                            <Text type="secondary">Description</Text>
                            <div>
                              <Text strong>{o.description}</Text>
                            </div>
                          </Col>
                          <Col span={8}>
                            <Text type="secondary">Date</Text>
                            <div>
                              <Text strong>{formatDate(o.date)}</Text>
                            </div>
                          </Col>
                          <Col span={8}>
                            <Text type="secondary">Amount</Text>
                            <div>
                              <Text strong>£{o.amount}</Text>
                            </div>
                          </Col>
                        </Row>
                      </Card>
                    ),
                  )}
                </div>
              )}
          </Collapse.Panel>
        )}

        {/* Step 5 */}
        {formDetails?.stepFive && (
          <Collapse.Panel
            header={
              <Title level={5} className="m-0 text-gray-800">
                Step 5: Child Benefits
              </Title>
            }
            key="step5"
          >
            {formDetails.stepFive.have_child_benefits &&
              formDetails.stepFive.child_benefits && (
                <Row
                  gutter={[16, 16]}
                  className="bg-gray-50/50 p-4 border rounded-lg"
                >
                  <Col xs={24} md={6}>
                    <Text type="secondary">No. of Children</Text>
                    <div>
                      <Text strong>
                        {formDetails.stepFive.child_benefits.no_children}
                      </Text>
                    </div>
                  </Col>
                  <Col xs={24} md={6}>
                    <Text type="secondary">Amount Received</Text>
                    <div>
                      <Text strong>
                        £{formDetails.stepFive.child_benefits.amount_received}
                      </Text>
                    </div>
                  </Col>
                  <Col xs={24} md={6}>
                    <Text type="secondary">Combined Income</Text>
                    <div>
                      <Text strong>
                        {formDetails.stepFive.child_benefits.combined_income}
                      </Text>
                    </div>
                  </Col>
                  <Col xs={24} md={6}>
                    <Text type="secondary">Partner Income</Text>
                    <div>
                      <Text strong>
                        £{formDetails.stepFive.child_benefits.partner_income}
                      </Text>
                    </div>
                  </Col>
                </Row>
              )}
          </Collapse.Panel>
        )}

        {/* Step 6 */}
        {formDetails?.stepSix && (
          <Collapse.Panel
            header={
              <Title level={5} className="m-0 text-gray-800">
                Step 6: Capital Gains
              </Title>
            }
            key="step6"
          >
            <Row gutter={[16, 16]} className="bg-gray-50/50 p-4 border rounded-lg mb-4">
              <Col xs={24} md={8}>
                <Text type="secondary">Has Capital Gains</Text>
                <div>
                  <Text strong>{formDetails.stepSix.has_capital_gains ? "Yes" : "No"}</Text>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary">Losses Brought Forward</Text>
                <div>
                  <Text strong>£{formDetails.stepSix.losses_brought_forward || 0}</Text>
                </div>
              </Col>
            </Row>
            {formDetails.stepSix.disposals && formDetails.stepSix.disposals.length > 0 && (
              <div>
                <Text strong className="text-gray-700">Disposals</Text>
                {formDetails.stepSix.disposals.map((d: any, i: number) => (
                  <Row gutter={[16, 16]} key={i} className="bg-gray-50/50 p-4 border rounded-lg mt-3">
                    <Col xs={24} md={8}>
                      <Text type="secondary">Asset Type</Text>
                      <div><Text strong>{d.asset_type}</Text></div>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text type="secondary">Description</Text>
                      <div><Text strong>{d.asset_description}</Text></div>
                    </Col>
                    <Col xs={24} md={8}>
                      <Text type="secondary">Disposal Date</Text>
                      <div><Text strong>{formatDate(d.disposal_date)}</Text></div>
                    </Col>
                    <Col xs={24} md={6}>
                      <Text type="secondary">Proceeds</Text>
                      <div><Text strong>£{d.disposal_proceeds || 0}</Text></div>
                    </Col>
                    <Col xs={24} md={6}>
                      <Text type="secondary">Allowable Costs</Text>
                      <div><Text strong>£{d.allowable_costs || 0}</Text></div>
                    </Col>
                    <Col xs={24} md={6}>
                      <Text type="secondary">Gain</Text>
                      <div><Text strong>£{d.gain || 0}</Text></div>
                    </Col>
                    <Col xs={24} md={6}>
                      <Text type="secondary">Loss</Text>
                      <div><Text strong>£{d.loss || 0}</Text></div>
                    </Col>
                  </Row>
                ))}
              </div>
            )}
          </Collapse.Panel>
        )}

        {/* Quarterly Data */}
        {quarterlyData && (
          <Collapse.Panel
            header={
              <Title level={5} className="m-0 text-gray-800">
                Quarterly Update{quarterlyData.quarter ? ` — ${quarterlyData.quarter}` : ""}
              </Title>
            }
            key="quarterly"
          >
            {quarterlyData.submitted_at && (
              <p className="text-xs text-gray-500 mb-4">
                Submitted to HMRC: {formatDate(quarterlyData.submitted_at)}
              </p>
            )}

            {/* Self Employment */}
            {quarterlyData.has_self_employment && quarterlyData.self_employment && (
              <div className="mb-6">
                <div className="flex gap-2 mb-3 items-center">
                  <div className="w-1 h-4 bg-primary rounded-full"></div>
                  <Text strong className="text-base text-gray-700">Self-Employment</Text>
                </div>
                <Row gutter={[16, 16]} className="bg-gray-50/50 p-4 border rounded-lg">
                  <Col xs={24} md={8}>
                    <Text type="secondary">Business ID</Text>
                    <div><Text strong className="font-mono text-xs">{quarterlyData.self_employment.business_id}</Text></div>
                  </Col>
                  <Col xs={24} md={8}>
                    <Text type="secondary">Turnover</Text>
                    <div><Text strong>£{quarterlyData.self_employment.turnover || 0}</Text></div>
                  </Col>
                  <Col xs={24} md={8}>
                    <Text type="secondary">Other Income</Text>
                    <div><Text strong>£{quarterlyData.self_employment.other_income || 0}</Text></div>
                  </Col>
                  {quarterlyData.self_employment.expenses && (
                    <Col xs={24}>
                      <Text type="secondary" className="block mb-2">Expenses</Text>
                      <Row gutter={[8, 8]}>
                        {Object.entries(quarterlyData.self_employment.expenses)
                          .filter(([, v]) => Number(v) > 0)
                          .map(([k, v]) => (
                            <Col xs={12} md={8} key={k}>
                              <Text type="secondary" className="text-xs capitalize">{k.replace(/_/g, ' ')}</Text>
                              <div><Text strong className="text-sm">£{String(v)}</Text></div>
                            </Col>
                          ))}
                      </Row>
                    </Col>
                  )}
                </Row>
              </div>
            )}

            {/* Property */}
            {quarterlyData.has_property && quarterlyData.property && (
              <div>
                <div className="flex gap-2 mb-3 items-center">
                  <div className="w-1 h-4 bg-primary rounded-full"></div>
                  <Text strong className="text-base text-gray-700">Property Income</Text>
                </div>
                <Row gutter={[16, 16]} className="bg-gray-50/50 p-4 border rounded-lg">
                  <Col xs={24} md={8}>
                    <Text type="secondary">Business ID</Text>
                    <div><Text strong className="font-mono text-xs">{quarterlyData.property.business_id}</Text></div>
                  </Col>
                  <Col xs={24} md={8}>
                    <Text type="secondary">Rent Income</Text>
                    <div><Text strong>£{quarterlyData.property.rent_income || 0}</Text></div>
                  </Col>
                  <Col xs={24} md={8}>
                    <Text type="secondary">Premiums</Text>
                    <div><Text strong>£{quarterlyData.property.premiums || 0}</Text></div>
                  </Col>
                  {quarterlyData.property.expenses && (
                    <Col xs={24}>
                      <Text type="secondary" className="block mb-2">Expenses</Text>
                      <Row gutter={[8, 8]}>
                        {Object.entries(quarterlyData.property.expenses)
                          .filter(([, v]) => Number(v) > 0)
                          .map(([k, v]) => (
                            <Col xs={12} md={8} key={k}>
                              <Text type="secondary" className="text-xs capitalize">{k.replace(/_/g, ' ')}</Text>
                              <div><Text strong className="text-sm">£{String(v)}</Text></div>
                            </Col>
                          ))}
                      </Row>
                    </Col>
                  )}
                </Row>
              </div>
            )}

            {/* HMRC submission results */}
            {quarterlyData.submission_results && Object.keys(quarterlyData.submission_results).length > 0 && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Text strong className="text-sm text-green-800">HMRC Submission Results</Text>
                {Object.entries(quarterlyData.submission_results).map(([k, v]) => (
                  <p key={k} className="text-xs text-green-700 mt-1">
                    <strong>{k}:</strong> {String(v)}
                  </p>
                ))}
              </div>
            )}
          </Collapse.Panel>
        )}
      </Collapse>
    </div>
  );
};

export default BucketDetails;
